import { supabase } from '@/config/supabase'

interface ConversationData {
  sessionId: string
  messages: { role: string; content: string }[]
  valuationCompleted: boolean
  leadId?: string
}

export async function saveConversation(data: ConversationData): Promise<void> {
  const { error } = await supabase
    .from('chat_conversations_sanremo')
    .upsert(
      {
        session_id: data.sessionId,
        messages: data.messages,
        message_count: data.messages.length,
        valuation_completed: data.valuationCompleted,
        lead_id: data.leadId || null,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'session_id' }
    )

  if (error) {
    console.error('Error saving conversation:', error)
    throw error
  }
}

export async function fetchConversations(limit = 50): Promise<
  {
    id: string
    session_id: string
    messages: { role: string; content: string }[]
    message_count: number
    valuation_completed: boolean
    lead_id: string | null
    created_at: string
    updated_at: string
  }[]
> {
  const { data, error } = await supabase
    .from('chat_conversations_sanremo')
    .select('*')
    .order('updated_at', { ascending: false })
    .limit(limit)

  if (error) {
    console.error('Error fetching conversations:', error)
    throw error
  }

  return data || []
}

export async function fetchChatAnalytics(): Promise<{
  totalConversations: number
  totalToday: number
  totalWeek: number
  valuationCompleted: number
  leadsFromChat: number
}> {
  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString()
  const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString()

  const [totalRes, todayRes, weekRes, valuationRes, leadsRes] = await Promise.all([
    supabase.from('chat_conversations_sanremo').select('id', { count: 'exact', head: true }),
    supabase.from('chat_conversations_sanremo').select('id', { count: 'exact', head: true }).gte('created_at', todayStart),
    supabase.from('chat_conversations_sanremo').select('id', { count: 'exact', head: true }).gte('created_at', weekStart),
    supabase.from('chat_conversations_sanremo').select('id', { count: 'exact', head: true }).eq('valuation_completed', true),
    supabase.from('leads_sanremo').select('id', { count: 'exact', head: true }).eq('source', 'ai_chat'),
  ])

  return {
    totalConversations: totalRes.count || 0,
    totalToday: todayRes.count || 0,
    totalWeek: weekRes.count || 0,
    valuationCompleted: valuationRes.count || 0,
    leadsFromChat: leadsRes.count || 0,
  }
}
